import { Component, inject, OnInit } from '@angular/core';
import { BehaviorSubject, Observable, of } from 'rxjs';
import { HttpHapService } from '@bo-client/core/http/http-hap.service';
import { DataShareService } from '@bo-client/shared/services/data-share.service';
import { Router } from '@angular/router';
import { ToastrService } from 'ngx-toastr';
import { FormArray, FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { GisDataModel } from '@bo-client/shared/models/gisData';
import { distinctUntilChanged, map, shareReplay, startWith, tap } from 'rxjs/operators';
import { CreateFormsService } from '@bo-client/activation-management/shared/forms/create-forms.service';
import {
  ActionTypeEnum,
  AreaTypeEnum
} from '@bo-client/activation-management/shared/models/commissioningModel';
import {
  AnswerWithMoreInfoModel,
  NavigateToEnum
} from '@bo-client/activation-management/shared/models/AnswerWithMoreInfoModel';
import { path } from '@bo-client/shared/constants/path';
import { ConnectionObjectForm } from '@bo-client/shared/models/connectionObject';
import { ContactDataForm } from '@bo-client/shared/models/contactDataDto';
import { GisDataEvent } from '@bo-client/shared/components/get-gis-data/get-gis-data.component';
import { ExternalCommissioningRequestModel } from '@bo-client/activation-management/shared/models/ExternalCommissioningRequestModel';
import { CompanyLocationDto } from '@bo-client/shared/models/companyLocations';
import { uri } from '@bo-client/shared/constants/uri';

@Component({
  selector: 'app-meter-inspection-page',
  templateUrl: './meter-inspection-page.component.html',
  styleUrls: ['./meter-inspection-page.component.scss']
})
export class MeterInspectionPageComponent implements OnInit {
  private fb = inject(FormBuilder);
  private httpHapService = inject(HttpHapService);
  private dataShareService = inject(DataShareService);
  private router = inject(Router);
  private createFormsService = inject(CreateFormsService);
  private toastService = inject(ToastrService);

  formGroup$: Observable<MeterInspectionForm>;
  summary$: Observable<ExternalCommissioningRequestModel>;

  companyLocationsByTenant$: Observable<CompanyLocationDto[]>;
  eventsFromParent$ = of('');
  selectedConnectionObject$: BehaviorSubject<GisDataModel> = new BehaviorSubject(null);

  ngOnInit(): void {
    this.companyLocationsByTenant$ = this.httpHapService.getTyped<CompanyLocationDto[]>(
      uri.companyLocationByCurrentTenant
    );
    this.formGroup$ = of(this.createFormGroup()).pipe(
      tap((formGroup) => {
        this.summary$ = formGroup.valueChanges.pipe(
          startWith(formGroup.value),
          distinctUntilChanged(),
          map((formValue) => this.formValueToDto(formValue)),
          shareReplay(1)
        );
      }),
      shareReplay(1)
    );
  }

  private createFormGroup(): MeterInspectionForm {
    return this.fb.group({
      customer: this.fb.array([this.createFormsService.createContactDataForm({})]),
      connectionObject: this.createFormsService.connectionObjectFormWithValidator({}, null),
      connectionObjectValid: [false, Validators.requiredTrue],
      technicalData: this.fb.group({
        exceedingExpectedConsumption: [false],
        causesNoise: [false],
        comment: ['']
      }),
      furtherInformation: this.fb.group({
        permissionToOpenDevice: [null as boolean | null, Validators.required],
        requestsAttendance: [false],
        acceptCostCondition: [false, Validators.requiredTrue],
        acceptCommissionCondition: [false, Validators.requiredTrue]
      }),
      acceptConditions: [false, Validators.requiredTrue]
    }) as MeterInspectionForm;
  }

  submit(formGroup: MeterInspectionForm) {
    if (formGroup.invalid) {
      console.debug('Befundprüfung form is invalid', formGroup, formGroup.value);
      formGroup.markAllAsTouched();
      if (formGroup.controls.acceptConditions.invalid) {
        this.toastService.error('Bitte akzeptieren Sie die Bedingungen.');
      }
      return;
    }

    const data = this.formValueToDto(formGroup.value);
    this.httpHapService
      .postTyped<AnswerWithMoreInfoModel>(data, '/external/commissioningrequest/inspection')
      .subscribe({
        next: (result) => {
          this.toastService.success('Vielen Dank, wir haben Ihre Daten erhalten.');
          this.navigateTo(result);
        },
        error: (err) => {
          console.error('Fehler beim Versenden des Antrags auf Befundprüfung', err);
          this.toastService.error('Beim Übermitteln ist ein Fehler aufgetreten: ' + err);
        }
      });
  }

  onGisDataChanged(
    $event: GisDataEvent & { currentCompanyLocationFromGis: CompanyLocationDto },
    validityControl: FormControl<boolean>
  ) {
    this.selectedConnectionObject$.next($event.gisData);
    validityControl.setValue($event.areaAvailable);
  }

  navigateTo(answer: AnswerWithMoreInfoModel) {
    if (answer.navigateTo !== NavigateToEnum.none) {
      this.router
        .navigate([path.external + '/' + answer.navigateTo + '/' + answer.message], {
          replaceUrl: true
        })
        .then();
    }
  }

  get netOperatorName(): string {
    return this.dataShareService.selectedTenant?.displayName || '';
  }

  private formValueToDto(formValue: MeterInspectionForm['value']): ExternalCommissioningRequestModel {
    const gisData = this.selectedConnectionObject$.getValue();
    const requestModel = new ExternalCommissioningRequestModel(
      ActionTypeEnum.inspection,
      null,
      AreaTypeEnum.POWER
    );
    const { customer, technicalData, furtherInformation } = formValue;
    requestModel.customer = customer[0] || {};
    requestModel.actions[0].inspection = {
      exceedingExpectedConsumption: technicalData?.exceedingExpectedConsumption || false,
      causesNoise: technicalData?.causesNoise || false,
      comment: technicalData?.comment || undefined,
      permissionToOpenDevice: furtherInformation?.permissionToOpenDevice ?? undefined,
      requestsAttendance: furtherInformation?.requestsAttendance || false
    };
    const { houseNo, houseNoAddition, ...rest } = gisData || {};
    requestModel.companyLocation.external_id = rest.orgEinheit;
    requestModel.connectionObject = { ...rest, streetNo: houseNo, streetNoAddition: houseNoAddition };
    return requestModel;
  }

  protected readonly AreaTypeEnum = AreaTypeEnum;
}

type MeterInspectionForm = FormGroup<{
  customer: FormArray<ContactDataForm>;
  connectionObject: ConnectionObjectForm;
  connectionObjectValid: FormControl<boolean>;
  technicalData: FormGroup<{
    exceedingExpectedConsumption: FormControl<boolean>;
    causesNoise: FormControl<boolean>;
    comment: FormControl<string>;
  }>;
  furtherInformation: FormGroup<{
    permissionToOpenDevice: FormControl<boolean | null>;
    requestsAttendance: FormControl<boolean>;
    acceptCostCondition: FormControl<boolean>;
    acceptCommissionCondition: FormControl<boolean>;
  }>;
  acceptConditions: FormControl<boolean>;
}>;
